import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText
} from "@mui/material";
import { IoLogOut, IoTrash, IoMenu, IoEllipsisVertical, IoArrowBack } from "react-icons/io5";
import { School as SchoolIcon, SportsEsports as SportsEsportsIcon, MenuBook as MenuBookIcon, SmartToy as SmartToyIcon } from '@mui/icons-material';
import { useNavigate } from "react-router-dom";
import { adminColors, radius, TOUCH_HIT_SX } from "../../theme/adminTheme";
import ConfirmDeleteChatDialog from "../ConfirmDeleteChatDialog";

// confirmDelete distinguishes two very different things this menu item has
// been asked to do depending on the surface it's rendered on:
//  - Chat.js passes the real, destructive soft-delete (useChatHistory's
//    clearChatHistory) - this needs the confirm dialog.
//  - StudentCurriculum.js passes plain navigate('/chat') to get back to the
//    chat view - there's nothing to delete, so routing it through the same
//    "this can't be undone, staff keep access for safety review" dialog
//    would be a false, alarming warning for a harmless back action.
const ChatHeader = ({ onLogout, onClearChat, onOpenSidebar, confirmDelete = true }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false);
  const [clearing, setClearing] = useState(false);
  const navigate = useNavigate();
  const open = Boolean(anchorEl);

  // A successful clear navigates away (back to /chat, which resolves to a
  // new thread) - this component can unmount mid-flight, so guard the
  // post-await state updates below.
  const isMountedRef = useRef(true);
  useEffect(() => () => { isMountedRef.current = false; }, []);

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleGamesHub = () => {
    navigate("/games");
    handleMenuClose();
  };

  const handleCurriculum = () => {
    navigate("/curriculum");
    handleMenuClose();
  };

  const handleAgentTutor = () => {
    navigate("/tutor");
    handleMenuClose();
  };

  const handleStudentGuide = () => {
    window.open('/guides/student-guide.html', '_blank');
    handleMenuClose();
  };

  const handleLogout = () => {
    onLogout();
    handleMenuClose();
  };

  const handleClearChatClick = () => {
    handleMenuClose();
    if (confirmDelete) {
      setClearConfirmOpen(true);
    } else {
      onClearChat();
    }
  };

  const handleCancelClearChat = () => setClearConfirmOpen(false);

  const handleConfirmClearChat = async () => {
    setClearing(true);
    await onClearChat();
    if (isMountedRef.current) {
      setClearing(false);
      setClearConfirmOpen(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        px: { xs: 1.5, sm: 3 },
        py: 2,
        gap: 1,
        backgroundColor: adminColors.surfaceRaised,
        borderBottom: "1px solid rgba(24,24,27,0.08)",
        position: "sticky",
        top: 0,
        zIndex: 1000
      }}
    >
      {/* Left side - sidebar toggle (below md, where the sidebar is an
          overlay Drawer instead of a persistent column) + Brand */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, minWidth: 0 }}>
        <IconButton
          onClick={onOpenSidebar}
          aria-label="Open chat list"
          sx={{
            display: { xs: "inline-flex", md: "none" },
            flexShrink: 0,
            ml: -0.5,
            color: adminColors.inkSoft,
            "&:hover": { backgroundColor: "rgba(79,70,229,0.08)", color: adminColors.accent },
            ...TOUCH_HIT_SX
          }}
        >
          <IoMenu size={20} />
        </IconButton>
        <Box
          sx={{
            width: 32,
            height: 32,
            flexShrink: 0,
            background: `linear-gradient(135deg, ${adminColors.accent}, ${adminColors.accentDark})`,
            borderRadius: '9px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <SchoolIcon sx={{ color: 'white', fontSize: 20 }} />
        </Box>
        <Typography
          variant="h6"
          noWrap
          sx={{
            fontWeight: 700,
            fontSize: "16px",
            color: adminColors.ink,
            letterSpacing: "-0.01em"
          }}
        >
          LearnAndMasterAI
        </Typography>
        <Typography
          variant="body2"
          noWrap
          sx={{
            display: { xs: 'none', sm: 'block' },
            color: adminColors.inkSoft,
            fontSize: "14px"
          }}
        >
          Your Learning Assistant
        </Typography>
      </Box>

      {/* Right side - overflow menu only. The staff dashboard link now
          lives permanently in the sidebar's bottom-left account pill, so
          it no longer needs a duplicate, always-visible control here. */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 }, flexShrink: 0 }}>
        <IconButton
          onClick={handleMenuClick}
          aria-label="More options"
          aria-haspopup="true"
          aria-expanded={open}
          sx={{
            color: adminColors.inkSoft,
            "&:hover": { backgroundColor: "rgba(79,70,229,0.08)", color: adminColors.accent },
            ...TOUCH_HIT_SX
          }}
        >
          <IoEllipsisVertical size={20} />
        </IconButton>
      </Box>

      {/* Dropdown Menu */}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        sx={{
          "& .MuiPaper-root": {
            borderRadius: radius.surface,
            minWidth: 180,
            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)"
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={handleGamesHub}>
          <ListItemIcon>
            <SportsEsportsIcon sx={{ fontSize: 18 }} />
          </ListItemIcon>
          <ListItemText>Games Hub</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleCurriculum}>
          <ListItemIcon>
            <MenuBookIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Class Materials</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleAgentTutor}>
          <ListItemIcon>
            <SmartToyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Agent Tutor</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleClearChatClick}>
          <ListItemIcon>
            {confirmDelete ? <IoTrash size={18} /> : <IoArrowBack size={18} />}
          </ListItemIcon>
          <ListItemText>{confirmDelete ? "Delete Chat" : "Back to Chat"}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <IoLogOut size={18} />
          </ListItemIcon>
          <ListItemText>Logout</ListItemText>
        </MenuItem>
      </Menu>
      {confirmDelete && (
        <ConfirmDeleteChatDialog
          open={clearConfirmOpen}
          onCancel={handleCancelClearChat}
          onConfirm={handleConfirmClearChat}
          loading={clearing}
        />
      )}
    </Box>
  );
};

export default ChatHeader;
